import React, { useCallback, useMemo } from 'react';
import { AuthStatus } from 'state/auth/reducer';
import { LargeButton } from 'components/LargeButton/LargeButton';
import { Link, useHistory } from 'react-router-dom';
import { loginAs, logout } from 'core/auth/auth';
import { makeStyles } from '@material-ui/core/styles';
import { makeVendorMenuHideAction } from 'state/vendorMenu/actions';
import { MenuItem, MenuItemProps } from 'components/MenuItem/MenuItem';
import { PromoButton } from 'components/PromoButton/PromoButton';
import { ReactComponent as UserIcon } from 'assets/icons/web-16/user-web--16.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/main-16/shape--16.svg';
import { ReactComponent as MarketingIcon } from 'assets/icons/additional/marketing-o--16.svg';
import { ReactComponent as PromoIcon } from 'assets/icons/main-24/gift--24.svg';
import { ReactComponent as ExitIcon } from 'assets/icons/main-16/shut-down--16.svg';
import { ReactComponent as LogoIcon } from 'assets/logo.svg';
import { RoutePath } from 'RoutePath';
import { Text, TextTypography } from 'components/Text/Text';
import { useAction } from 'core/store/store';
import { SupportedCountryCodes, UserRole } from 'types';
import { useSelector } from 'react-redux';
import i18n from '../../../../i18n';
import { WhatsappAskQuestion } from 'components/WhatsappAskQuestion/WhatsappAskQuestion';

import {
    authRoleSelector,
    authStatusSelector,
    authStoreIdSelector,
    authStoreOpenForOrdersSelector,
} from 'state/auth/selectors';
import { SelectRegionControl } from '../../../components/SelectRegionControl/SelectRegionControl';
import { SelectLanguageControl } from '../../../components/SelectLanguageControl/SelectLanguageControl';
import browserCookies from "browser-cookies";
import { appConfig } from "../../../config/app";
import { getClientUrl, isSubdomain } from 'core/utils/utils';

const useStyles = makeStyles(theme => ({
    container: {
        overflowY: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    mainHeader: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'space-between',
        padding: 16,
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
    },
    header: {
        display: 'flex',
        padding: theme.spacing(2.5),
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
    },
    title: {
        paddingRight: theme.spacing(3),
        width: '100%',
        textAlign: 'center',
    },
    promoButton: {
        cursor: 'pointer',
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
    },
    items: {
        flex: 4,
    },
    footer: {
        padding: theme.spacing(1),
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(3),
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignContent: 'center',
    },
    document: {
        height: 55,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',
    },
    social: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignContent: 'center',
    },
    socialIcon: {
        margin: theme.spacing(1),
    },
    profile: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    contents: {
        flex: 1,
    },
    loginAsButton: {
        padding: theme.spacing(3),
        paddingLeft: theme.spacing(2),
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
    },
    settings: {
        display: 'flex',
        gap: 8,
    },
    westernDocs: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: theme.spacing(1)
    },
    westernDocsItem: {
        marginBottom: theme.spacing(1),
    }
}));

const isServer = typeof window === 'undefined';

export const VendorMenu = () => {
    const {
        t,
    } = i18n.useTranslation();
    const isoCode = !isServer ? browserCookies?.get('country') : SupportedCountryCodes.RUS;
    const isRusOrUzb = [SupportedCountryCodes.RUS, SupportedCountryCodes.UZB].includes(
        isoCode as SupportedCountryCodes,
    );
    
    const marketplacePhoneNumber = isoCode !== SupportedCountryCodes.RUS
        ? appConfig.marketplacePhoneNumber
        : appConfig.marketplacePhoneNumberRu;
    const history = useHistory();
    const isLoggedIn = AuthStatus.LOGGED_IN === useSelector(authStatusSelector);
    const openForOrders = useSelector(authStoreOpenForOrdersSelector);
    const hasStore = useSelector(authStoreIdSelector) != null;
    const currentRole = useSelector(authRoleSelector);

    const action = useAction();
    const menuItemClickAction = useCallback(clickAction => {
        action(makeVendorMenuHideAction);
        clickAction.call();
    }, []);

    const menuItems: MenuItemProps[] = useMemo(() => {
        return isLoggedIn
            ? [
                  {
                      title: t('common:userMenu.profile.title'),
                      iconComponent: UserIcon,
                      onClick: () => history.push(RoutePath.VENDOR_MENU_PROFILE),
                  },
                  {
                      title: t('common:userMenu.settings'),
                      iconComponent: SettingsIcon,
                      onClick: () => history.push(RoutePath.VENDOR_MENU_STORE_INFO),
                      badgeText: !openForOrders && t('onboarding:settings.noOrders'),
                  },
                  {
                      title: t('common:userMenu.marketing'),
                      iconComponent: MarketingIcon,
                      onClick: () => history.push(RoutePath.VENDOR_MENU_MARKETING_MATERIALS),
                  },
                  {
                      title: t('vendor:header.promo'),
                      iconComponent: PromoIcon,
                      svgSize: 24,
                      onClick: () => history.push(RoutePath.VENDOR_PROMO),
                  },
                  {
                      title: t('common:userMenu.exit'),
                      iconComponent: ExitIcon,
                      onClick: () => {
                          logout(RoutePath.FEED);
                      },
                  },
              ]
            : [
                  {
                      title: t('common:userMenu:login'),
                      iconComponent: UserIcon,
                      onClick: () => history.push(RoutePath.LOGIN),
                  },
              ];
    }, [isLoggedIn, t, history, openForOrders]);

    const handlePromoClick = useCallback(() => {
        action(makeVendorMenuHideAction);
        if (isLoggedIn) {
            loginAs(UserRole.VENDOR);
        } else {
            history.push(RoutePath.SIGNUP);
        }
    }, [isLoggedIn, history]);

    const loginAsCustomer = () => {
        window.location.assign(getClientUrl());
    }

    const classes = useStyles();
    return (
        <div className={classes.container}>
            <div>
                <div className={classes.mainHeader}>
                    <LogoIcon />
                    <div className={classes.settings}>
                        {!isSubdomain() && <SelectRegionControl />}
                        <SelectLanguageControl />
                    </div>
                </div>
                {!hasStore && (
                    <div className={classes.promoButton}>
                        <PromoButton onClick={handlePromoClick} />
                    </div>
                )}
                {hasStore && (
                    <div className={classes.loginAsButton}>
                        <LargeButton
                            onClick={loginAsCustomer}
                        >
                            {t('common:userMenu:loginAsCustomer')}
                        </LargeButton>
                    </div>
                )}
                <div className={classes.items}>
                    {menuItems.map(
                        (item: MenuItemProps) =>
                            (!item.visibleForRole || item.visibleForRole === currentRole) && (
                                <MenuItem
                                    key={item.title}
                                    visibleForRole={item.visibleForRole}
                                    iconComponent={item.iconComponent}
                                    title={item.title}
                                    svgSize={item.svgSize}
                                    badgeText={item.badgeText}
                                    onClick={() => {
                                        menuItemClickAction(item.onClick);
                                    }}
                                />
                            ),
                    )}
                </div>
            </div>
            <div>
                <WhatsappAskQuestion marketplacePhoneNumber={marketplacePhoneNumber} />
                {isRusOrUzb ? (
                    <Link to={RoutePath.LEGAL}>
                        <div className={classes.document}>
                            <Text typography={TextTypography.BODY_ACCENT}>{t('common:userMenu.documents')}</Text>
                        </div>
                    </Link>
                ) : (
                    <div className={classes.westernDocs}>
                        <a 
                            href={'/legal-documents/Terms_of_Service_anymarket.pdf'} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={classes.westernDocsItem}
                        >
                            <Text typography={TextTypography.BODY_ACCENT}>{t('legal:termsOfService')}</Text>
                        </a>
                        <a 
                            href={'/legal-documents/Privacy_Policy_any.market.pdf'} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={classes.westernDocsItem} 
                        >
                            <Text typography={TextTypography.BODY_ACCENT}>{t('legal:privacyPolicy')}</Text>
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};
